'use client'

// Morphing-pill navbar — always visible.
// At top (scrollY ≤ 60): full-width, transparent, no panel.
// Scrolled:              centered glass pill 16px from top, fit-content,
//                        rounded, blurred, subtle shadow.
// The morph is pure CSS transitions on min-width / max-width / padding /
// border-radius / background / box-shadow. Constant `width: max-content`
// keeps the element sized by its content at every interpolation step.

import { useEffect, useRef, useState, useCallback } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import gsap from 'gsap'
import { useReducedMotion } from '@/lib/animations/useReducedMotion'
import { useDarkSection } from './useDarkSection'
import { useLocale } from './useLocale'
import NavLogo from './NavLogo'
import NavLinks from './NavLinks'
import LangToggle from './LangToggle'
import ContactButton from './ContactButton'
import MobileMenu from './MobileMenu'

// Sections whose visible background stays dark for their entire duration.
// `transition-bridge` is intentionally NOT here: it morphs from dark to
// cream during scroll, and listing it would force a cream-on-cream nav
// during its long cream phase. The brief dark intro of the bridge falls
// back to the navbar's normal styling.
const DARK_SECTIONS = ['problem', 'contact']
const PILL_THRESHOLD = 60   // scrollY past which the pill activates
// Routes that are dark end-to-end (no light sections to cross). On these,
// the navbar must use the light/cream foreground regardless of scroll
// position — the section-watcher can't see anything to flip it on.
const DARK_ROUTES = ['/about', '/sobre-nosotros', '/contact', '/contacto']

// (No longer scrolling to a home-page anchor — Contacto now routes to the
// dedicated Contact page at /contact (or /contacto in Spanish).)

interface NavProps {
  logoUrl?: string
}

export default function Nav({ logoUrl }: NavProps = {}) {
  const navRef = useRef<HTMLElement>(null)
  const reduced = useReducedMotion()
  const router = useRouter()
  const pathname = usePathname()

  const [pillActive, setPillActive] = useState(false)
  // Dark-section detection has to account for the pill's top offset + height.
  // Section watcher fails on routes whose sections don't share IDs with the
  // home page — for those (e.g. /about, /contact) we force dark via the
  // route-level fallback below.
  const isDarkSection = useDarkSection(DARK_SECTIONS, pillActive ? 76 : 80)
  const [isMobile, setIsMobile] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  // When the mobile menu is open, the dark overlay covers everything below
  // the nav — so the logo + hamburger must read as light/cream regardless
  // of the underlying section.
  const isDark = isDarkSection || DARK_ROUTES.includes(pathname) || (isMobile && menuOpen)
  const [locale, setLocale] = useLocale()

  // Viewport breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handle = (m: MediaQueryList | MediaQueryListEvent) => {
      setIsMobile(m.matches)
      if (!m.matches) setMenuOpen(false)
    }
    handle(mq)
    mq.addEventListener('change', handle)
    return () => mq.removeEventListener('change', handle)
  }, [])

  // Scroll → pillActive. On refresh mid-page, snap to pill WITHOUT transition.
  useEffect(() => {
    const el = navRef.current
    if (!el) return

    if (window.scrollY > PILL_THRESHOLD) {
      el.classList.add('no-transition')
      setPillActive(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.classList.remove('no-transition')
        })
      })
    }

    let pending = false
    let raf = 0
    const compute = () => {
      pending = false
      setPillActive(window.scrollY > PILL_THRESHOLD)
    }
    const onScroll = () => {
      if (pending) return
      pending = true
      raf = requestAnimationFrame(compute)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(raf)
    }
  }, [])

  // Entrance animation (page load)
  useEffect(() => {
    const el = navRef.current
    if (!el) return
    if (reduced) {
      gsap.set(el, { autoAlpha: 1, y: 0 })
      return
    }
    gsap.set(el, { autoAlpha: 0, y: -30 })
    const items = el.querySelectorAll<HTMLElement>('[data-nav-anim]')
    if (items.length) gsap.set(items, { autoAlpha: 0, y: -10 })
    const tl = gsap.timeline({ delay: 0.4 })
    tl.to(el, { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out' })
    if (items.length) {
      tl.to(items, { autoAlpha: 1, y: 0, duration: 0.4, ease: 'power2.out', stagger: 0.08 }, '-=0.3')
    }
  }, [reduced])

  // Contact button routes to the dedicated Contact page. Locale-aware:
  // Spanish visitors land on /contacto, everyone else on /contact.
  const handleContact = useCallback(() => {
    const target = locale === 'es' ? '/contacto' : '/contact'
    if (pathname !== target) router.push(target)
  }, [pathname, router, locale])

  return (
    <>
      <style>{`
        /* ── Morphing glass pill ──────────────────────────────────────
         * width: max-content stays constant; min-width/max-width are the
         * animated constraints. At top, min-width: 100% forces the nav to
         * fill the viewport; in pill, min-width: 0 lets it shrink to
         * content. max-width interpolates from 100% → 1100px. Because
         * both endpoints are interpolatable lengths, the computed width
         * animates smoothly through every intermediate frame.
         */
        .nav-glass {
          position: fixed;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          z-index: 100;

          min-width: 100%;
          max-width: 100%;
          width: max-content;

          height: 80px;
          display: flex;
          justify-content: center;
          align-items: center;

          background: transparent;
          border: 1px solid transparent;
          border-radius: 0;
          box-shadow: none;

          transition:
            top 500ms cubic-bezier(0.4, 0, 0.2, 1),
            min-width 500ms cubic-bezier(0.4, 0, 0.2, 1),
            max-width 500ms cubic-bezier(0.4, 0, 0.2, 1),
            height 500ms cubic-bezier(0.4, 0, 0.2, 1),
            background 400ms ease,
            border-color 400ms ease,
            border-radius 500ms cubic-bezier(0.4, 0, 0.2, 1),
            box-shadow 400ms ease,
            backdrop-filter 400ms ease,
            -webkit-backdrop-filter 400ms ease;
        }
        .nav-glass.nav--pill {
          top: 16px;
          min-width: 0;
          max-width: 1100px;
          height: 60px;
          background: rgba(245, 238, 217, 0.72);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border-color: rgba(81, 50, 5, 0.08);
          border-radius: 100px;
          box-shadow: 0 8px 32px rgba(81, 50, 5, 0.12);
        }
        .nav-glass.nav--pill.nav--dark {
          background: rgba(31, 22, 8, 0.72);
          border-color: rgba(245, 238, 217, 0.08);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
        }

        .nav-inner {
          width: 100%;
          max-width: 1400px;
          height: 100%;
          padding: 0 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 40px;
          transition:
            max-width 500ms cubic-bezier(0.4, 0, 0.2, 1),
            padding 500ms cubic-bezier(0.4, 0, 0.2, 1),
            gap 500ms cubic-bezier(0.4, 0, 0.2, 1);
        }
        .nav-glass.nav--pill .nav-inner {
          max-width: 1100px;
          padding: 0 20px 0 8px;
          gap: 24px;
        }

        /* Suppress transitions on mount (refresh mid-page) */
        .nav-glass.no-transition,
        .nav-glass.no-transition *,
        .nav-glass.no-transition .nav-inner {
          transition: none !important;
        }

        /* ── Mobile ──────────────────────────────────────────────── */
        @media (max-width: 767px) {
          .nav-glass { height: 64px; }
          .nav-glass.nav--pill { top: 12px; height: 52px; }
          .nav-inner { padding: 0 20px; }
          .nav-glass.nav--pill .nav-inner { padding: 0 8px 0 16px; gap: 16px; }
        }

        /* ── Safari / old browsers without backdrop-filter ───────── */
        @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
          .nav-glass.nav--pill           { background: rgba(245, 238, 217, 0.95); }
          .nav-glass.nav--pill.nav--dark { background: rgba(31, 22, 8, 0.95); }
        }

        /* ── Nav link underline & focus ──────────────────────────── */
        .nav-link::after {
          content: '';
          position: absolute;
          left: 0; right: 0; bottom: 0;
          height: 1px;
          background: #9FAF08;
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 300ms ease, transform-origin 0s 300ms;
        }
        .nav-link:hover::after {
          transform: scaleX(1);
          transform-origin: left center;
          transition: transform 300ms ease;
        }
        .nav-link[data-active]::after { transform: scaleX(0); }
        .nav-link:hover { color: var(--nav-link-hover); }
        .nav-link:focus-visible,
        button:focus-visible {
          outline: 2px solid #9FAF08;
          outline-offset: 3px;
          border-radius: 2px;
        }
        .lang-toggle:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 14px rgba(81,50,5,0.14);
        }

        /* ── Hamburger → X ───────────────────────────────────────── */
        .hamburger {
          border-radius: 50% !important;
        }
        .hamburger:focus-visible {
          outline: 2px solid rgba(159,175,8,0.6) !important;
          outline-offset: 2px !important;
        }
        .hamburger span {
          display: block;
          width: 22px; height: 2px;
          margin: 3px auto;
          transition: transform 300ms cubic-bezier(0.22,1,0.36,1),
                      opacity 200ms ease,
                      background 300ms ease;
          border-radius: 1px;
          transform-origin: center;
        }
        .hamburger[data-open="true"] span:nth-child(1) { transform: translateY(5px) rotate(45deg); }
        .hamburger[data-open="true"] span:nth-child(2) { opacity: 0; transform: scaleX(0); }
        .hamburger[data-open="true"] span:nth-child(3) { transform: translateY(-5px) rotate(-45deg); }

        @media (prefers-reduced-motion: reduce) {
          .nav-glass, .nav-inner { transition: none !important; }
          .nav-link::after { transition: none !important; }
        }
      `}</style>

      <header
        ref={navRef}
        className={[
          'nav-glass',
          isDark ? 'nav--dark' : '',
          pillActive ? 'nav--pill' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="nav-inner">
          {/* LEFT — logo */}
          <div data-nav-anim>
            <NavLogo isDark={isDark} isScrolled={pillActive} logoUrl={logoUrl} />
          </div>

          {/* CENTER — links (desktop only) */}
          {!isMobile && (
            <div data-nav-anim>
              <NavLinks isDark={isDark} />
            </div>
          )}

          {/* RIGHT */}
          <div
            data-nav-anim
            style={{ display: 'flex', alignItems: 'center', gap: 16 }}
          >
            {!isMobile && (
              <>
                <LangToggle locale={locale} onChange={setLocale} isDark={isDark} />
                <ContactButton onClick={handleContact} />
              </>
            )}
            {isMobile && (
              <button
                type="button"
                className="hamburger"
                data-open={menuOpen || undefined}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                onClick={() => setMenuOpen(o => !o)}
                style={{
                  width: 44, height: 44,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ background: isDark ? '#F5ECD8' : '#513205' }} />
                <span style={{ background: isDark ? '#F5ECD8' : '#513205' }} />
                <span style={{ background: isDark ? '#F5ECD8' : '#513205' }} />
              </button>
            )}
          </div>
        </div>
      </header>

      {isMobile && (
        <MobileMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          locale={locale}
          setLocale={setLocale}
          onContact={handleContact}
        />
      )}
    </>
  )
}
